// Utility for managing debug menu UI state with localStorage persistence

// LocalStorage keys for debug menu UI state
const DEBUG_MENU_UI_STATE_KEY = 'automation-game-debug-menu-ui-state';
const DEBUG_CONFIRMATION_STATE_KEY = 'automation-game-debug-confirmation-state';

// Debug menu UI state interface
export interface DebugMenuUIState {
  debugMenuOpen: boolean;
}

// Debug confirmation state interface
export interface DebugConfirmationState {
  confirmationCount: number;
  isConfirmed: boolean;
}

// Default states
const DEFAULT_DEBUG_MENU_UI_STATE: DebugMenuUIState = {
  debugMenuOpen: false,
};

const DEFAULT_DEBUG_CONFIRMATION_STATE: DebugConfirmationState = {
  confirmationCount: 0,
  isConfirmed: false,
};

// Load debug menu UI state from localStorage
export const loadDebugMenuUIState = (): DebugMenuUIState => {
  try {
    const saved = localStorage.getItem(DEBUG_MENU_UI_STATE_KEY);
    if (saved) {
      const state = JSON.parse(saved) as DebugMenuUIState;
      return {
        debugMenuOpen:
          state.debugMenuOpen ?? DEFAULT_DEBUG_MENU_UI_STATE.debugMenuOpen,
      };
    }
  } catch {
    // If loading fails, use defaults
  }
  return DEFAULT_DEBUG_MENU_UI_STATE;
};

// Save debug menu UI state to localStorage
export const saveDebugMenuUIState = (state: DebugMenuUIState): void => {
  try {
    localStorage.setItem(DEBUG_MENU_UI_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore localStorage errors
  }
};

// Load debug confirmation state from localStorage
export const loadDebugConfirmationState = (): DebugConfirmationState => {
  try {
    const saved = localStorage.getItem(DEBUG_CONFIRMATION_STATE_KEY);
    if (saved) {
      const state = JSON.parse(saved) as DebugConfirmationState;
      return {
        confirmationCount:
          state.confirmationCount ??
          DEFAULT_DEBUG_CONFIRMATION_STATE.confirmationCount,
        isConfirmed:
          state.isConfirmed ?? DEFAULT_DEBUG_CONFIRMATION_STATE.isConfirmed,
      };
    }
  } catch {
    // If loading fails, use defaults
  }
  return DEFAULT_DEBUG_CONFIRMATION_STATE;
};

// Save debug confirmation state to localStorage
export const saveDebugConfirmationState = (
  state: DebugConfirmationState
): void => {
  try {
    localStorage.setItem(DEBUG_CONFIRMATION_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore localStorage errors
  }
};

// Clear debug confirmation state (for testing/reset purposes)
export const clearDebugConfirmationState = (): void => {
  try {
    localStorage.removeItem(DEBUG_CONFIRMATION_STATE_KEY);
  } catch {
    // Ignore localStorage errors
  }
};
